/**
 * Condition Analyzer Models
 * 
 * Shared types and interfaces for the condition analyzer algorithm.
 * Used by both frontend and backend scripts.
 */

/**
 * The operational conditions that can be detected
 */
export enum Condition {
    NON_EXISTENCE = 'NON_EXISTENCE',
    DANGER = 'DANGER',
    EMERGENCY = 'EMERGENCY',
    NORMAL = 'NORMAL',
    AFFLUENCE = 'AFFLUENCE',
    POWER = 'POWER',
    UNKNOWN = 'UNKNOWN'
}

/**
 * Trend direction
 */
export type TrendDirection = 'up' | 'down' | 'stable' | 'volatile';

/**
 * Information about a condition for display purposes
 */
export interface ConditionInfo {
    name: string;
    description: string;
    color: string;
    icon: string;
    order: number;
}

/**
 * Condition information lookup
 */
export const CONDITION_INFO: Record<Condition, ConditionInfo> = {
    [Condition.NON_EXISTENCE]: {
        name: 'Non-Existence',
        description: 'No meaningful activity or output',
        color: '#9E9E9E',
        icon: 'remove_circle_outline',
        order: 1
    },
    [Condition.DANGER]: {
        name: 'Danger',
        description: 'Sharp decline, things going wrong',
        color: '#F44336',
        icon: 'warning',
        order: 2
    },
    [Condition.EMERGENCY]: {
        name: 'Emergency',
        description: 'Crisis point, recovery needed or declining from high',
        color: '#FF9800',
        icon: 'emergency',
        order: 3
    },
    [Condition.NORMAL]: {
        name: 'Normal Operation',
        description: 'Stable performance within expected range',
        color: '#4CAF50',
        icon: 'check_circle',
        order: 4
    },
    [Condition.AFFLUENCE]: {
        name: 'Affluence',
        description: 'Sharp rise, rapid improvement',
        color: '#8BC34A',
        icon: 'trending_up',
        order: 5
    },
    [Condition.POWER]: {
        name: 'Power',
        description: 'Sustained high performance',
        color: '#2196F3',
        icon: 'bolt',
        order: 6
    },
    [Condition.UNKNOWN]: {
        name: 'Unknown',
        description: 'Unable to determine condition',
        color: '#757575',
        icon: 'help_outline',
        order: 0
    }
};

/**
 * A single data point for analysis
 */
export interface AnalyzerDataPoint {
    date: Date;
    value: number;
    valueCompounded?: number;
    index?: number;
}

/**
 * Serializable version of AnalyzerDataPoint for Firestore storage
 */
export interface AnalyzerDataPointFirestore {
    date: string; // ISO string
    value: number;
    valueCompounded?: number;
    index?: number;
}

/**
 * Configuration options for the analyzer
 */
export interface ConditionAnalyzerConfig {
    /** The baseline value for comparison (default: 50) */
    baseline: number;
    /** Variance percent for stability check (default: 0.10 = 10%) */
    stabilityVariancePercent: number;
    /** Percent change to qualify as "sharp rise" (default: 0.25 = 25%) */
    sharpRisePercent: number;
    /** Percent change to qualify as "sharp drop" (default: 0.25 = 25%) */
    sharpDropPercent: number;
    /** Threshold below which is Non-Existence (default: 10) */
    nonExistenceThreshold: number;
    /** Threshold above which qualifies for Power (default: 80) */
    powerThreshold: number;
    /** Minimum data points required for analysis (default: 5) */
    minDataPoints: number;
    
    // Slope thresholds (in % per period) - DEPRECATED: use angle thresholds instead
    /** Slope below this = Danger (default: -5) */
    slopeDangerThreshold: number;
    /** Slope below this but above Danger = Emergency (default: -1) */
    slopeEmergencyThreshold: number;
    /** Slope above this = Affluence (default: 1) */
    slopeAffluenceThreshold: number;
    /** Slope above this = Power (default: 5) */
    slopePowerThreshold: number;
    
    // Visual angle thresholds (in degrees)
    // These are more intuitive: -90° = vertical drop, 0° = flat, +90° = vertical rise
    /** Angle below this = Non-Existence (steepest crash, default: -60) */
    angleNonExistenceThreshold: number;
    /** Angle below this = Danger (steep decline, default: -30) */
    angleDangerThreshold: number;
    /** Angle below this = Emergency (gradual decline, default: -5) */
    angleEmergencyThreshold: number;
    /** Angle above this = Affluence (rising, default: 15) */
    angleAffluenceThreshold: number;
    /** Angle above this = Power (steep rise, default: 45) */
    anglePowerThreshold: number;
    
    // Zone angle ranges (min/max for each condition)
    // These define the exact bounds of each colored zone
    /** Non-Existence zone: min angle (default: -89) */
    angleNonExistenceMin: number;
    /** Non-Existence zone: max angle (default: -60) */
    angleNonExistenceMax: number;
    /** Danger zone: min angle (default: -60) */
    angleDangerMin: number;
    /** Danger zone: max angle (default: -30) */
    angleDangerMax: number;
    /** Emergency zone: min angle (default: -30) */
    angleEmergencyMin: number;
    /** Emergency zone: max angle (default: -5) */
    angleEmergencyMax: number;
    /** Normal zone: min angle (default: -5) */
    angleNormalMin: number;
    /** Normal zone: max angle (default: 15) */
    angleNormalMax: number;
    /** Affluence zone: min angle (default: 15) */
    angleAffluenceMin: number;
    /** Affluence zone: max angle (default: 45) */
    angleAffluenceMax: number;
    /** Power zone: min angle (default: 45) */
    anglePowerMin: number;
    /** Power zone: max angle (default: 89) */
    anglePowerMax: number;
    
    /** Minimum consecutive points in power zone to qualify as POWER condition (default: 3) */
    minPowerZonePoints: number;
    
    /** Use fixed 0-based scale for angle calculation instead of auto-scale (default: false) */
    useFixedScaleForAngle: boolean;
}

/**
 * Default configuration
 */
export const DEFAULT_ANALYZER_CONFIG: ConditionAnalyzerConfig = {
    baseline: 50,
    stabilityVariancePercent: 0.10,
    sharpRisePercent: 0.25,
    sharpDropPercent: 0.25,
    nonExistenceThreshold: 10,
    powerThreshold: 0.80,  // 80% of max value = Power
    minDataPoints: 5,
    // Slope thresholds (in % per period) - DEPRECATED
    slopeDangerThreshold: -5,
    slopeEmergencyThreshold: -1,
    slopeAffluenceThreshold: 1,
    slopePowerThreshold: 5,
    // Visual angle thresholds (in degrees)
    // -90° = vertical drop, 0° = flat, +90° = vertical rise
    angleNonExistenceThreshold: -60,  // Steepest crash (near vertical)
    angleDangerThreshold: -30,         // Steep decline  
    angleEmergencyThreshold: -5,       // Gradual decline
    angleAffluenceThreshold: 15,       // Rising trend
    anglePowerThreshold: 45,           // Steep rise (power)
    
    // Zone angle ranges (min/max for each condition)
    angleNonExistenceMin: -89,
    angleNonExistenceMax: -60,
    angleDangerMin: -60,
    angleDangerMax: -30,
    angleEmergencyMin: -30,
    angleEmergencyMax: -5,
    angleNormalMin: -5,
    angleNormalMax: 15,
    angleAffluenceMin: 15,
    angleAffluenceMax: 45,
    anglePowerMin: 45,
    anglePowerMax: 89,
    
    // Power zone sustained detection
    minPowerZonePoints: 3,  // At least 3 consecutive ending points in power zone = POWER
    
    // Angle calculation mode
    useFixedScaleForAngle: false  // false = auto-scale (yMin-yMax), true = fixed 0-based scale
};

/**
 * Statistical metrics calculated from the data
 */
export interface AnalysisMetrics {
    // Regression
    slope: number;
    intercept: number;
    rSquared: number;
    
    // Central tendency
    mean: number;
    median: number;
    
    // Dispersion
    variance: number;
    standardDeviation: number;
    coefficientOfVariation: number;
    
    // Range
    min: number;
    max: number;
    range: number;
    
    // Change
    startValue: number;
    endValue: number;
    absoluteChange: number;
    percentChange: number;
    rateOfChange: number;
    
    // Position
    distanceFromBaseline: number;
    percentOfMax: number;
    
    // Recovery
    hasRecovery: boolean;
    recoveryStartIndex: number | null;
    recoveryPercent: number;
    lowestPointIndex: number;
    highestPointIndex: number;
    
    // Volatility
    volatilityScore: number;
    
    // Visual angle of trend line (in degrees)
    // -90° = vertical drop, 0° = flat, +90° = vertical rise
    visualAngle: number;
}

/**
 * A factor that influences condition determination
 */
export interface ConditionFactor {
    name: string;
    value: number | boolean | string;
    weight: number;
    supports: Condition[];
}

/**
 * Complete result of the analysis
 */
export interface ConditionAnalysisResult {
    condition: Condition;
    conditionInfo: ConditionInfo;
    confidence: number;
    trendDirection: TrendDirection;
    metrics: AnalysisMetrics;
    explanation: string;
    factors: ConditionFactor[];
    dataPoints: AnalyzerDataPoint[];
    trendLine: { x: Date; y: number }[];
}

/**
 * Test scenario definition
 */
export interface TestScenario {
    id: string;
    name: string;
    description: string;
    expectedCondition: Condition;
    dataPoints: AnalyzerDataPoint[];
}

/**
 * Firestore-storable version of TestScenario
 */
export interface TestScenarioFirestore {
    id: string;
    name: string;
    description: string;
    expectedCondition: Condition;
    dataPoints: AnalyzerDataPointFirestore[];
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Result of running a test
 */
export interface TestResult {
    scenario: TestScenario;
    result: ConditionAnalysisResult;
    passed: boolean;
    expectedCondition: Condition;
    detectedCondition: Condition;
}
